import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as Logo } from "assets/images/house_scan_logo.svg";
import { ReactComponent as LogoWithoutName } from "assets/images/house_scan_logo_whitout_name.svg";
import { ReactComponent as SearchIcon } from "assets/icons/search_main.svg";
import { ReactComponent as FiltterIcon } from "assets/icons/filter.svg";
import avatar from "assets/images/avatar.png";
import { useLocation, useNavigate } from "react-router";
import useDialog from "hooks/useDialog";
import useAuth from "hooks/useAuth";
import useSubscription from "hooks/useSubscription";
import PropertyFilterDropdown from "components/DropDown/PropertyTypeFilterDropdown";
import SearchDropdown from "components/DropDown/SearchDropdown";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import useSearch from "hooks/useSearch";
import { useMediaQuery } from "react-responsive";
import { AnimatePresence, motion } from "framer-motion";
import SearchMobileFilter from "pages/Research/components/SearchMobileFilter";
import {
  capitalizeWord,
  formatLocationName,
  isObjectEmpty,
} from "utils/commonUtils";
import { useSearchParams } from "react-router-dom";
import { findLocationFromUrl } from "utils/searchFilter";
import locations from "pages/Map/locations.json";
import { v4 as uuidv4 } from "uuid";
import SearchInput from "pages/Research/components/SearchInput";

const MainHeader = ({ isSearchVisable = true, isSearchScreen }) => {
  const navigation = useNavigate();
  const { openSigninDialog } = useDialog();
  const { user } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const {
    selectedLocations,
    setSelectedLocations,
    showSearchDropdown,
    searchTerm,
    handleSearchChange,
    openSearchDropdown,
    toggleFilterDropdown,
    handleRemoveLocation,
  } = useSearch();
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [isSticky, setIsSticky] = useState(true);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const isSearchMobile = useMediaQuery({ query: "(max-width: 1024px)" });
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const inputRef = useRef();
  const buttonRef = useRef(null);

  // Memoize the search and mobile conditional styles and classes
  const isSearchAndMobile = useMemo(
    () => isSearchScreen && isSearchMobile,
    [isSearchScreen, isSearchMobile]
  );

  // mobile sticky header
  useEffect(() => {
    if (!isMobile) {
      setIsSticky(true);
      return;
    }
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      // If scrolling up and it's below a certain threshold (e.g., not at the very top), make it sticky
      if (scrollTop < lastScrollTop && scrollTop > 100) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }

      if (!isSearchAndMobile && scrollTop <= 100) {
        setIsSticky(true);
      }

      if (isSearchAndMobile && scrollTop <= 165) {
        setIsSticky(true);
      }

      setLastScrollTop(scrollTop);
    };
    window.addEventListener("scroll", handleScroll);
    // Clean up the event listener on component unmount
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollTop, isMobile, isSearchMobile]);

  // Initilalize searched location from url
  useEffect(() => {
    const locationIdParams = searchParams.getAll("locationId") || [];
    const searchedLocations = findLocationFromUrl(locationIdParams);
    const mapLocations = searchParams.getAll("mapLocation");

    const mapLocationsName = mapLocations.map((location) => ({
      name: formatLocationName(location),
      id: uuidv4(),
    }));

    const allSelectedLocations = [...searchedLocations, ...mapLocationsName];

    if (
      allSelectedLocations.length === 0 &&
      location.pathname === "/research"
    ) {
      setSelectedLocations([{ id: 0, zip: "", name: "Milano" }]);
    } else {
      setSelectedLocations(allSelectedLocations);
    }
  }, [searchParams]);

  // auto focus input
  useEffect(() => {
    if (showSearchDropdown && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearchDropdown]);

  return (
    <AnimatePresence>
      <motion.header
        initial={{
          y: isMobile ? (isSearchAndMobile ? -174 : -100) : 0,
          opacity: isMobile ? 0 : 1,
        }}
        animate={{
          y: isSticky ? 0 : isSearchAndMobile ? -174 : -100,
          opacity: isSticky ? 1 : 0,
        }}
        exit={{ y: isSearchAndMobile ? -174 : -100, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className={`w-full h-[88px] top-0 bg-white z-[51]  flex items-center justify-between sticky`}
        style={{
          display: isSearchScreen && isMobile ? "block" : "flex",
          height: isSearchScreen && isMobile ? 158 : 88,
        }}
      >
        <div
          style={{
            borderBottom: isSearchAndMobile ? "1px solid #EAECF0" : "none",
          }}
          className="flex items-center justify-between h-[88px] w-full pl-[16px] md:pl-[19px] pr-[19px] lg:pr-10"
        >
          <div className="relative">
            <Logo
              onClick={() => navigation("/")}
              className="w-[146px] cursor-pointer h-[31px] md:w-[232px] md:h-[49px]"
            />
            {/* <div className="bg-primary absolute -top-[6px] md:top-0 -right-[21px] border-[0.5px] border-primary logo-beta-shadow rounded-[4px] w-[36px] h-[12px] flex items-center justify-center text-white text-[8px] font-semibold">
              BETA
            </div> */}

            {/* Trial Crown Badge */}
            {user &&
              subscriptionStatus?.isInTrial &&
              subscriptionStatus?.trialDaysRemaining > 0 && (
                <div className="absolute -top-[15px] md:-top-[12px] -right-[17px] md:-right-[25px] border-dashed border-[1px] border-primary rounded-full w-[25px] h-[25px] md:w-[30px] md:h-[30px] flex items-center justify-center">
                  <span className="text-[10px] md:text-[12px] font-bold">
                    {subscriptionStatus.trialDaysRemaining}g
                  </span>
                </div>
              )}
          </div>

          <div className="flex items-center">
            {isSearchVisable && (!isMobile || isSearchAndMobile) && (
              <div className="bg-[#F5F5F5] relative  flex w-full md:w-[350px] lg:w-[550px] xl:w-[870px] h-[56px] md:h-[60px] space-x-5 rounded-[10px] md:rounded-[15px] pr-[6px] justify-between items-center">
                <div
                  onClick={openSearchDropdown}
                  className="flex h-full pl-4 relative flex-1 items-center space-x-2"
                >
                  <SearchIcon
                    className="cursor-pointer"
                    width={20}
                    height={20}
                  />
                  <div className="flex-1 h-full flex items-center justify-start space-x-2">
                    {selectedLocations.length > 0 && (
                      <div className="flex px-[7px] py-[4px] max-w-[180px] xs:max-w-[280px] cursor-pointer items-center space-x-2 bg-[#456AF21A]  rounded-[5px] h-[32px]">
                        <p className="text-[#456AF2] text-[18px] truncate flex-1 font-[200] ">
                          {selectedLocations.length > 1
                            ? `Milano • ${selectedLocations.length} zone selezionate`
                            : selectedLocations[0].name}
                        </p>
                        <div
                          onClick={handleRemoveLocation}
                          className="!w-[14px] min-w-[14px] !h-[14px] rounded-full bg-[#EFE9F6] flex items-center justify-center"
                        >
                          <CloseIcon width={8} height={8} fill="#456AF2" />
                        </div>
                      </div>
                    )}
                    <input
                      className="outline-none w-full bg-transparent"
                      placeholder={selectedLocations.length < 1 ? "Cerca" : ""}
                      ref={inputRef}
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                    <SearchDropdown
                      dropdonwStyle={{
                        top: 62,
                        borderRadius: "20px",
                        marginLeft: 0,
                      }}
                    />
                  </div>
                </div>
                <div
                  ref={buttonRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFilterDropdown("searchHeaderFilter");
                  }}
                  className="bg-white hidden md:block rounded-[10px] cursor-pointer p-3 w-[48px] h-[48px]"
                >
                  <FiltterIcon width={24} height={24} />
                </div>
                <PropertyFilterDropdown
                  style={{ right: 0, top: 63 }}
                  id="searchHeaderFilter"
                  buttonRef={buttonRef}
                />
              </div>
            )}
          </div>

          <button
            onClick={() =>
              user ? navigation("/settings") : openSigninDialog()
            }
            className={`${
              user
                ? "bg-transparent text-primary hover:bg-primary hover:text-white "
                : "bg-primary text-white hover:bg-transparent hover:text-primary"
            } hidden md:inline-flex w-[151px] h-[46px] items-center justify-center rounded-[23px] border-[3px] border-primary font-medium text-[18px]  transition-all duration-300 ease-in-out`}
          >
            {user ? `Ciao ${user?.firstName}` : "Iscriviti"}
          </button>

          <div className="flex items-center md:hidden space-x-[23px]">
            {isSearchVisable && (
              <div className="w-[31px] h-[31px] flex items-center justify-center">
                <SearchIcon width={20} height={20} className="cursor-pointer" />
              </div>
            )}

            {user ? (
              user?.avatar ? (
                <img
                  src={user?.avatar}
                  alt="avatar"
                  className="rounded-full w-[25px] h-[25px] object-cover cursor-pointer"
                />
              ) : (
                <div className="rounded-full w-[25px] h-[25px] flex items-center justify-center bg-primary font-bold text-[14px] text-white cursor-pointer">
                  {user?.firstName?.charAt(0)?.toUpperCase()}
                </div>
              )
            ) : (
              <img
                onClick={openSigninDialog}
                src={avatar}
                alt="default avatar"
                className="rounded-full w-[25px] h-[25px] object-cover cursor-pointer"
              />
            )}
          </div>
        </div>

        {/* {isSearchAndMobile && <SearchMobileFilter />} */}
        {isSearchAndMobile && (
          <>
            <div className="pt-3 pl-[16px] md:pl-[19px] pr-[19px] lg:pr-10">
              <SearchInput />
            </div>
            <div className="mobile-nav-shadow" />
          </>
        )}
      </motion.header>
    </AnimatePresence>
  );
};

export default MainHeader;
